import {
    Button, Card, Col, ConfigProvider, DatePicker, Descriptions, Empty, Modal, Row,
    Select, Space, Spin, Table, Tag, Tooltip, Typography
} from "antd";
import frFR from "antd/locale/fr_FR";
import { useEffect, useMemo, useState } from "react";
import {
    ReloadOutlined, EyeOutlined, SyncOutlined, CheckCircleTwoTone, CloseCircleTwoTone
} from "@ant-design/icons";
import dayjs from "dayjs";

import axiosInstance from "../../variables/api/axiosInstance";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import { frenchDate } from "../../variables/functions";
import { displayNumbers } from "../../variables/numbersFunctions";
import { StatCard } from "../../components/statistics/StatCard";
import { COLORS } from "../../components/ui/colors";
import { rightsService } from "../../services/rightsService";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const SOURCE_COLORS = { STCA: "geekblue", DTRF: "purple" };
const TYPE_COLORS = { CONTRATS: "blue", REGLEMENTS: "cyan", VERSEMENTS: "gold", DLA: "magenta" };
const STATUS_META = {
    SUCCES: { color: "green", label: "Succès" },
    PARTIEL: { color: "orange", label: "Partiel" },
    ECHEC: { color: "red", label: "Échec" },
};

const SuiviSynchronisations = () => {
    const canView = rightsService("HLA083");
    const [datas, setDatas] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [stats, setStats] = useState({});
    const [dateF, setDateF] = useState([]);
    const [sourceFilter, setSourceFilter] = useState(null);
    const [typeFilter, setTypeFilter] = useState(null);
    const [statutFilter, setStatutFilter] = useState(null);

    const [detail, setDetail] = useState(null);

    const fetchData = () => {
        setLoadingData(true);
        axiosInstance.get(`/api/sync/logs`, {
            params: {
                page: 0,
                size: 1000,
                source: sourceFilter || null,
                type: typeFilter || null,
                status: statutFilter || null,
                startDate: dateF && dateF[0] ? dateF[0] : null,
                endDate: dateF && dateF.length > 1 ? dateF[1] : null,
            },
        })
            .then((res) => {
                setDatas(res.data?.content || []);
                setLoadingData(false);
            })
            .catch(() => setLoadingData(false));
        axiosInstance.get(`/api/sync/logs/stats`).then((res) => setStats(res.data || {})).catch(() => { });
    };

    useEffect(() => { if (canView) fetchData(); /* eslint-disable-next-line */ }, [dateF, sourceFilter, typeFilter, statutFilter]);

    const indicateurs = useMemo(() => {
        const total = datas.length;
        const appliques = datas.reduce((s, d) => s + (d.createdCount || 0), 0);
        const echecs = datas.filter((d) => d.status === "ECHEC").length;
        return { total, appliques, echecs };
    }, [datas]);

    const fmtDuree = (ms) => {
        if (ms == null) return "-";
        return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
    };

    const columns = [
        { title: "Date", dataIndex: "startedAt", key: "startedAt", render: (v) => (v ? frenchDate(v) : "-") },
        {
            title: "Source", dataIndex: "source", key: "source",
            render: (v) => <Tag color={SOURCE_COLORS[v] || "default"}>{v}</Tag>,
        },
        {
            title: "Flux", dataIndex: "type", key: "type",
            render: (v) => <Tag color={TYPE_COLORS[v] || "default"}>{v}</Tag>,
        },
        { title: "Reçus", dataIndex: "receivedCount", key: "receivedCount", align: "right", render: (v) => displayNumbers(v || 0) },
        { title: "Appliqués", dataIndex: "createdCount", key: "createdCount", align: "right", render: (v) => <Text strong style={{ color: COLORS.success }}>{displayNumbers(v || 0)}</Text> },
        { title: "Ignorés", dataIndex: "skippedCount", key: "skippedCount", align: "right", render: (v) => displayNumbers(v || 0) },
        { title: "Erreurs", dataIndex: "errorCount", key: "errorCount", align: "right", render: (v) => (v ? <Text strong style={{ color: "#cf1322" }}>{displayNumbers(v)}</Text> : "0") },
        {
            title: "Statut", dataIndex: "status", key: "status",
            render: (v) => { const m = STATUS_META[v] || { color: "default", label: v }; return <Tag color={m.color}>{m.label}</Tag>; },
        },
        { title: "Durée", dataIndex: "durationMs", key: "durationMs", align: "right", render: (v) => fmtDuree(v) },
        {
            title: "", key: "actions", width: 50,
            render: (_, r) => (
                <Tooltip title="Détail">
                    <Button size="small" shape="circle" icon={<EyeOutlined />} onClick={() => setDetail(r)} />
                </Tooltip>
            ),
        },
    ];

    const lastStca = stats?.lastStca ? frenchDate(stats.lastStca) : "—";

    if (!canView) {
        return (
            <div class="layout-wrapper layout-content-navbar">
                <div class="layout-container">
                    <SideBar currentMenu={"SYNCHRONISATIONS"} />
                    <div class="layout-page">
                        <NavBar />
                        <div class="content-wrapper">
                            <div class="container-xxl flex-grow-1 container-p-y">
                                <Empty
                                    style={{ marginTop: 80 }}
                                    description="Vous n'avez pas l'habilitation requise (HLA083) pour consulter les synchronisations."
                                />
                            </div>
                            <Footer />
                            <div class="content-backdrop fade"></div>
                        </div>
                    </div>
                </div>
                <div class="layout-overlay layout-menu-toggle"></div>
                <div class="drag-target"></div>
            </div>
        );
    }

    return (
        <div class="layout-wrapper layout-content-navbar">
            <div class="layout-container">
                <SideBar currentMenu={"SYNCHRONISATIONS"} />
                <div class="layout-page">
                    <NavBar />
                    <div class="content-wrapper">
                        <div class="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div style={{
                                background: COLORS.gradient, borderRadius: 16, padding: "22px 26px",
                                marginBottom: 18, color: "#fff",
                                display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12,
                            }}>
                                <div>
                                    <Title level={3} style={{ color: "#fff", margin: 0 }}><SyncOutlined /> Suivi des synchronisations</Title>
                                    <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                        Traces des synchronisations reçues des applications satellites (STCA, DTRF)
                                    </Text>
                                </div>
                                <Space wrap>
                                    <RangePicker
                                        format="DD/MM/YYYY"
                                        value={dateF && dateF[0] ? [dayjs(dateF[0]), dayjs(dateF[1])] : null}
                                        onChange={(v) => setDateF(v ? [v[0].format("YYYY-MM-DD"), v[1].format("YYYY-MM-DD")] : [])}
                                        placeholder={["Date début", "Date fin"]}
                                    />
                                    <Tooltip title="Rafraîchir">
                                        <Button icon={<ReloadOutlined />} onClick={fetchData} />
                                    </Tooltip>
                                </Space>
                            </div>

                            {/* Indicateurs */}
                            <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
                                <Col xs={12} md={6}>
                                    <StatCard title="Synchronisations" subtitle="période filtrée"
                                        value={`${indicateurs.total}`} icon="bx bx-sync"
                                        iconBg={COLORS.primary} gradient={COLORS.primary} />
                                </Col>
                                <Col xs={12} md={6}>
                                    <StatCard title="Éléments appliqués" subtitle="créés / mis à jour"
                                        value={`${displayNumbers(indicateurs.appliques)}`} icon="bx bx-check-double"
                                        iconBg={COLORS.success} gradient={COLORS.success} />
                                </Col>
                                <Col xs={12} md={6}>
                                    <StatCard title="Échecs" subtitle="période filtrée"
                                        value={`${indicateurs.echecs}`} icon="bx bx-error"
                                        iconBg={COLORS.warning} gradient={COLORS.warning} />
                                </Col>
                                <Col xs={12} md={6}>
                                    <StatCard title="Dernière synchro STCA" subtitle={stats?.lastStcaStatus || "—"}
                                        value={lastStca} icon="bx bx-time-five"
                                        iconBg={COLORS.secondary} gradient={COLORS.secondary} />
                                </Col>
                            </Row>

                            {/* Filtres */}
                            <Card bordered={false} style={{ borderRadius: 14, marginBottom: 16, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
                                bodyStyle={{ padding: 16 }}>
                                <Row gutter={[12, 12]} align="middle">
                                    <Col xs={24} md={8}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Source</Text>
                                        <Select allowClear style={{ width: "100%" }} placeholder="Toutes les sources"
                                            value={sourceFilter} onChange={setSourceFilter}
                                            options={[{ label: "STCA", value: "STCA" }, { label: "DTRF", value: "DTRF" }]} />
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Flux</Text>
                                        <Select allowClear style={{ width: "100%" }} placeholder="Tous les flux"
                                            value={typeFilter} onChange={setTypeFilter}
                                            options={[
                                                { label: "Contrats", value: "CONTRATS" },
                                                { label: "Règlements", value: "REGLEMENTS" },
                                                { label: "Versements", value: "VERSEMENTS" },
                                                { label: "Lots d'attestations", value: "DLA" },
                                            ]} />
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Statut</Text>
                                        <Select allowClear style={{ width: "100%" }} placeholder="Tous les statuts"
                                            value={statutFilter} onChange={setStatutFilter}
                                            options={[
                                                { label: "Succès", value: "SUCCES" },
                                                { label: "Partiel", value: "PARTIEL" },
                                                { label: "Échec", value: "ECHEC" },
                                            ]} />
                                    </Col>
                                </Row>
                            </Card>

                            {/* Tableau */}
                            <Card bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
                                bodyStyle={{ padding: 8 }}>
                                <ConfigProvider locale={frFR}>
                                    <Spin spinning={loadingData}>
                                        <Table
                                            columns={columns}
                                            dataSource={datas}
                                            rowKey="id"
                                            size="middle"
                                            locale={{ emptyText: <Empty description="Aucune synchronisation sur la période" /> }}
                                            pagination={{ showSizeChanger: true, defaultPageSize: 15, showTotal: (t) => `${t} synchronisation(s)` }}
                                        />
                                    </Spin>
                                </ConfigProvider>
                            </Card>
                        </div>
                        <Footer />
                        <div class="content-backdrop fade"></div>
                    </div>
                </div>
            </div>

            {/* Détail */}
            <Modal
                title={detail ? <Space>{detail.status === "ECHEC" ? <CloseCircleTwoTone twoToneColor="#cf1322" /> : <CheckCircleTwoTone twoToneColor="#52c41a" />} Détail de la synchronisation #{detail.id}</Space> : ""}
                centered
                open={!!detail}
                onCancel={() => setDetail(null)}
                footer={[<Button key="close" type="primary" onClick={() => setDetail(null)}>Fermer</Button>]}
                width={760}
            >
                {detail ? (
                    <>
                        <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="Source"><Tag color={SOURCE_COLORS[detail.source] || "default"}>{detail.source}</Tag></Descriptions.Item>
                            <Descriptions.Item label="Flux"><Tag color={TYPE_COLORS[detail.type] || "default"}>{detail.type}</Tag></Descriptions.Item>
                            <Descriptions.Item label="Début">{detail.startedAt ? frenchDate(detail.startedAt) : "-"}</Descriptions.Item>
                            <Descriptions.Item label="Fin">{detail.finishedAt ? frenchDate(detail.finishedAt) : "-"}</Descriptions.Item>
                            <Descriptions.Item label="Durée">{fmtDuree(detail.durationMs)}</Descriptions.Item>
                            <Descriptions.Item label="Statut">{(STATUS_META[detail.status] || {}).label || detail.status}</Descriptions.Item>
                            <Descriptions.Item label="Reçus">{displayNumbers(detail.receivedCount || 0)}</Descriptions.Item>
                            <Descriptions.Item label="Appliqués">{displayNumbers(detail.createdCount || 0)}</Descriptions.Item>
                            <Descriptions.Item label="Ignorés">{displayNumbers(detail.skippedCount || 0)}</Descriptions.Item>
                            <Descriptions.Item label="Erreurs">{displayNumbers(detail.errorCount || 0)}</Descriptions.Item>
                            <Descriptions.Item label="IP source" span={2}>{detail.sourceIp || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Message" span={2}>{detail.message || "-"}</Descriptions.Item>
                        </Descriptions>
                        {detail.errorDetails ? (
                            <div style={{ marginTop: 12 }}>
                                <Text type="secondary">Détails de l'erreur</Text>
                                <pre style={{ background: "#fff1f0", border: "1px solid #ffccc7", borderRadius: 8, padding: 10, marginTop: 4, maxHeight: 240, overflow: "auto", fontSize: 12, whiteSpace: "pre-wrap" }}>
                                    {detail.errorDetails}
                                </pre>
                            </div>
                        ) : null}
                    </>
                ) : null}
            </Modal>

            <div class="layout-overlay layout-menu-toggle"></div>
            <div class="drag-target"></div>
        </div>
    );
};

export default SuiviSynchronisations;
